import { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Maximize2 } from '@digital-twins/design-system/icons'
import {
  KnowledgeAskCitation,
  KnowledgeDocumentListItem,
  WorkflowRun,
  WorkflowTemplate,
} from '@digital-twins/shared-types'
import { AssistantCalculationEvidence } from '@digital-twins/shared-types/assistant'

import { CreativeMedia } from '@/components/chat/CreativeMedia'
import {
  StudyDocPlotLookup,
  StudyDocQuote,
} from '@/components/study-doc'
import { StudyDocStripLookup } from '@/components/study-doc/StudyDocMagazineBand/resolveMagazineBandSource'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { ThreadAssetView } from '@/lib/api'
import { ROUTES } from '@/routes/routes'
import {
  WorkflowGateSubmission,
  WorkflowRunError,
} from '@/lib/workflows/useWorkflowRun'

import { ThreadCalculationSources } from '../ThreadCalculationSources'
import { useArtifactStudyDoc } from '../useArtifactStudyDoc'
import { STUDY_DOC_SLUG, ThreadArtifactItem } from './collectThreadArtifacts'
import { ArtifactCategoryTitle } from './ArtifactCategoryTitle'
import { ThreadArtifactBodyPreview } from './ThreadArtifactBodyPreview'
import { ThreadAssetTiles } from './ThreadAssetTiles'
import { ThreadRunPane } from './ThreadRunPane'

/**
 * Stacked collapsibles inside the thread's one sources drawer.
 *
 * Artifacts (living thread library), Assets (thread stimuli registry),
 * Context (the newest answer's calculations), and Progress (live run
 * watch-only). Citations are not listed here: each answer carries its own
 * source chips, and the drawer only stacks what spans the thread. Progress is the ONE
 * step surface: the template recipe used to be listed beside it and said the
 * same thing twice. The citation viewer still replaces this whole surface when
 * a source tab is open — that swap lives on the page.
 *
 * Rail cards are toggle + fullscreen only. Edit / copy / print stay on
 * expanded and fullscreen views. A report tied to a study also offers
 * Open report, which opens that study in the pane, and a separate
 * Open in new tab control.
 */

type RailSectionId = 'outputs' | 'assets' | 'context' | 'progress'

interface ThreadArtifactPaneProps {
  /** Living library items (already mapped + media-enriched). */
  artifacts: Array<ThreadArtifactItem>
  run: WorkflowRun | null
  template: WorkflowTemplate | null
  isLoading: boolean
  error?: WorkflowRunError | null
  /** Living-library SWR load state (distinct from run pane loading). */
  livingIsLoading?: boolean
  /** Living-library fetch error — must not look like an empty library. */
  livingError?: unknown
  /** Re-fetch living artifacts (retry control). */
  onRetryLiving?: () => void
  /**
   * Unconfirmed creative draft versions across open draft sets.
   * Count only; omit or 0 hides the status line. No Confirm here.
   */
  pendingCreativeDraftCount?: number
  /** Live / refused-read progress section. Hidden when false. */
  showProgress: boolean
  /** P1: the calculations behind the newest answer, shown under Context. */
  calculations?: Array<AssistantCalculationEvidence>
  /**
   * P1: the figure chip the reader followed. Each new click (nonce) opens
   * Context and reveals that calculation.
   */
  focusedCalculation?: { evidenceId: string; nonce: number } | null
  docsByToken: Map<string, KnowledgeDocumentListItem>
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
  /** Absent for a viewer of a shared thread: no step offers a retry. */
  onRetryStep?: (stepId: string) => void
  onSubmitGate: (submission: WorkflowGateSubmission) => void
  onCancelRun: () => void
  /** Thread / answer language for seeded Progress titles. */
  language?: string | null
  /** Opens the full-viewport artifact surface from a rail card. */
  onOpenArtifactFullscreen?: (item: ThreadArtifactItem) => void
  /** Opens a study report artifact in the thread study pane. */
  onOpenStudyReport?: (studyToken: string) => void
  assets?: Array<ThreadAssetView>
  onRemoveAsset?: (assetToken: string) => void
  /**
   * StudyDoc embed lookups, same set the expanded and fullscreen surfaces
   * take. Without them a `{{plot:id}}` embed renders as its own placeholder
   * text and the reader sees a raw uuid where the chart belongs. The peek
   * draws the real chart, small: its size is what says "expand to read it".
   */
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  populationId?: string
  strips?: Record<string, StudyDocStripLookup>
}

interface RailCollapsibleProps {
  label: string
  toggleTestId: string
  expanded: boolean
  onToggle: () => void
  children: ReactNode
  sectionTestId?: string
  /** Quiet status under the section label (visible even when collapsed). */
  statusChrome?: ReactNode
}

function preferredOpenSection(args: {
  artifactCount: number
  hasAssets: boolean
  hasSources: boolean
  showProgress: boolean
}): RailSectionId {
  const { artifactCount, hasAssets, hasSources, showProgress } = args

  if (artifactCount > 0) {
    return 'outputs'
  }
  if (hasAssets) {
    return 'assets'
  }
  if (hasSources) {
    return 'context'
  }
  if (showProgress) {
    return 'progress'
  }
  return 'outputs'
}

function RailCollapsible(props: RailCollapsibleProps) {
  const {
    label,
    toggleTestId,
    expanded,
    onToggle,
    children,
    sectionTestId,
    statusChrome,
  } = props

  return (
    <section className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        className="group flex w-full cursor-pointer items-center justify-between gap-2 px-1 py-2.5 text-left"
        data-testid={toggleTestId}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <Text
          as="span"
          size="sm"
          weight="medium"
          className="transition-colors group-hover:text-foreground"
        >
          {label}
        </Text>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-[transform,color] group-hover:text-foreground',
            expanded ? 'rotate-180' : '',
          )}
          aria-hidden
        />
      </button>
      {statusChrome ? (
        <div className={cn('px-1', expanded ? 'pb-1' : 'pb-2.5')}>
          {statusChrome}
        </div>
      ) : null}
      {expanded ? (
        <div className="pb-3" data-testid={sectionTestId}>
          {children}
        </div>
      ) : null}
    </section>
  )
}

interface ArtifactCardProps {
  item: ThreadArtifactItem
  index: number
  onOpenFullscreen?: (item: ThreadArtifactItem) => void
  threadAssets?: Array<ThreadAssetView>
  docsByToken: Map<string, KnowledgeDocumentListItem>
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
  onOpenStudyReport?: (studyToken: string) => void
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  populationId?: string
  strips?: Record<string, StudyDocStripLookup>
}

function ArtifactCard(props: ArtifactCardProps) {
  const {
    item,
    index,
    onOpenFullscreen,
    threadAssets,
    docsByToken,
    onOpenCitation,
    onOpenStudyReport,
    plots,
    quotes,
    populationId,
    strips,
  } = props

  const { t } = useTranslation('app')

  const [expanded, setExpanded] = useState(false)
  const isStimulus = item.kind === 'stimulus'
  /* Same gate the expanded and fullscreen surfaces use: the lookups are
     composed from ONE run's findings, so handing them to any other body would
     resolve embeds against the wrong study. */
  /* Lookups for THIS artifact's own run. The pane is handed the current run's
     study-doc lookups, which are the right ones only when the reader is
     looking at the current run's study doc: a report from an earlier run, or
     any body carrying plot tokens that is not the study doc, was left with
     `Plot: <uuid>` boxes over content that was one fetch away. */
  const ownStudyDoc = useArtifactStudyDoc(item)
  const isStudyDoc = item.slug === STUDY_DOC_SLUG
  const cardPlots = ownStudyDoc?.plots ?? (isStudyDoc ? plots : undefined)
  const cardQuotes = ownStudyDoc?.quotes ?? (isStudyDoc ? quotes : undefined)
  const cardPopulationId =
    ownStudyDoc?.populationId ?? (isStudyDoc ? populationId : undefined)
  const cardStrips = ownStudyDoc?.strips ?? (isStudyDoc ? strips : undefined)

  return (
    <div
      className="rounded-lg border border-border/60 bg-muted/20"
      data-testid={`thread-artifact-card-${index}`}
    >
      <div className="flex items-center gap-1 px-1">
        <button
          type="button"
          className="group flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 px-2 py-2.5 text-left"
          data-testid={`thread-artifact-card-toggle-${index}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          <ArtifactCategoryTitle item={item} />
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-[transform,color] group-hover:text-foreground',
              expanded ? 'rotate-180' : '',
            )}
            aria-hidden
          />
        </button>
        {onOpenFullscreen ? (
          <button
            type="button"
            className="shrink-0 rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            data-testid={`thread-artifact-card-fullscreen-${index}`}
            aria-label={t('threads.detail.rail.expandFullscreenAria', {
              name: item.title,
            })}
            onClick={() => onOpenFullscreen(item)}
          >
            <Maximize2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {item.sourceStudyToken && onOpenStudyReport ? (
        <div className="flex flex-wrap items-center gap-2 px-3 pb-2.5">
          <button
            type="button"
            className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
            data-testid={`thread-artifact-study-report-${item.publicId ?? index}`}
            onClick={() => {
              if (item.sourceStudyToken) {
                onOpenStudyReport(item.sourceStudyToken)
              }
            }}
          >
            {t('threads.sessions.openReport')}
          </button>
          <a
            href={ROUTES.studyResults(item.sourceStudyToken)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
            data-testid={`thread-artifact-study-new-tab-${item.publicId ?? index}`}
          >
            {t('threads.sessions.openInNewTab')}
          </a>
        </div>
      ) : null}
      {expanded ? (
        <div
          className="border-t border-border/50 px-3 py-2.5"
          data-testid={`thread-artifact-card-body-${index}`}
        >
          {isStimulus && item.mediaSrc ? (
            <div
              className="overflow-hidden rounded-md"
              data-testid={`thread-artifact-stimulus-${index}`}
            >
              <CreativeMedia
                src={item.mediaSrc}
                mimeType={item.mimeType}
                dataTestId={`thread-artifact-stimulus-media-${index}`}
              />
            </div>
          ) : (
            <ThreadArtifactBodyPreview
              body={item.body}
              density="compact"
              threadAssets={threadAssets}
              docsByToken={docsByToken}
              onOpenCitation={onOpenCitation}
              plots={cardPlots}
              quotes={cardQuotes}
              populationId={cardPopulationId}
              strips={cardStrips}
              runToken={item.runToken}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}

export interface ThreadArtifactCardListProps
  extends Omit<ArtifactCardProps, 'item' | 'index'> {
  items: Array<ThreadArtifactItem>
  /** First card index, so two lists on one pane keep unique test ids. */
  indexOffset?: number
}

/**
 * A12: the rail cards for one section of the side pane's list (Reports or
 * Artifacts), the same cards this pane stacks under its own Artifacts header.
 */
export function ThreadArtifactCardList(props: ThreadArtifactCardListProps) {
  const { items, indexOffset = 0, ...cardProps } = props

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <ArtifactCard
          key={item.key}
          item={item}
          index={indexOffset + index}
          {...cardProps}
        />
      ))}
    </div>
  )
}

export function ThreadArtifactPane(props: ThreadArtifactPaneProps) {
  const {
    artifacts,
    run,
    template,
    isLoading,
    error = null,
    livingIsLoading = false,
    livingError = null,
    onRetryLiving,
    pendingCreativeDraftCount = 0,
    showProgress,
    calculations = [],
    focusedCalculation = null,
    docsByToken,
    onOpenCitation,
    onRetryStep,
    onSubmitGate,
    onCancelRun,
    language = null,
    onOpenArtifactFullscreen,
    onOpenStudyReport,
    assets,
    onRemoveAsset,
    plots,
    quotes,
    populationId,
    strips,
  } = props

  const { t } = useTranslation('app')
  const { t: tCommon } = useTranslation('common')

  const hasSources = calculations.length > 0
  /* Stimuli already shown as living artifact cards are excluded from the
     Assets badge count so the same file is not double-counted. */
  const stimulusAssetTokens = new Set(
    artifacts
      .filter((item) => item.kind === 'stimulus' && item.assetToken)
      .map((item) => item.assetToken as string),
  )
  const assetsForBadge =
    assets?.filter((asset) => !stimulusAssetTokens.has(asset.token)) ?? []
  const hasAssets = Boolean(assets && assets.length > 0)
  const assetsBadgeCount = assetsForBadge.length

  const userTouchedRef = useRef(false)

  const [openSections, setOpenSections] = useState<Set<RailSectionId>>(() => {
    const preferred = preferredOpenSection({
      artifactCount: artifacts.length,
      hasAssets,
      hasSources,
      showProgress,
    })
    return new Set<RailSectionId>([preferred])
  })

  useEffect(() => {
    if (userTouchedRef.current) {
      return
    }
    const preferred = preferredOpenSection({
      artifactCount: artifacts.length,
      hasAssets,
      hasSources,
      showProgress,
    })
    if (preferred !== 'outputs' || artifacts.length === 0) {
      return
    }
    setOpenSections((current) => {
      if (current.has('outputs')) {
        return current
      }
      return new Set<RailSectionId>(['outputs'])
    })
  }, [artifacts.length, hasAssets, hasSources, showProgress])

  const focusNonce = focusedCalculation?.nonce
  useEffect(() => {
    if (focusNonce === undefined) {
      return
    }
    userTouchedRef.current = true
    setOpenSections((current) => {
      if (current.has('context')) {
        return current
      }
      return new Set(current).add('context')
    })
  }, [focusNonce])

  function isSectionOpen(section: RailSectionId): boolean {
    return openSections.has(section)
  }

  function handleToggleSection(section: RailSectionId) {
    userTouchedRef.current = true
    setOpenSections((current) => {
      const next = new Set(current)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })
  }

  const artifactsLabel =
    artifacts.length > 0
      ? t('threads.detail.rail.artifactsCount', { count: artifacts.length })
      : t('threads.detail.rail.artifacts')
  const assetsLabel =
    assetsBadgeCount > 0
      ? t('threads.detail.rail.assetsCount', { count: assetsBadgeCount })
      : t('threads.detail.rail.assets')

  const livingFailed = Boolean(livingError) && !livingIsLoading

  const pendingDraftsStatus =
    pendingCreativeDraftCount > 0 ? (
      <Text
        size="sm"
        color="muted"
        data-testid="thread-artifacts-pending-drafts"
      >
        {t('threads.detail.rail.pendingCreativeDrafts', {
          count: pendingCreativeDraftCount,
        })}
      </Text>
    ) : null

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3 pt-2"
      data-testid="thread-artifact-pane"
    >
      <RailCollapsible
        label={artifactsLabel}
        toggleTestId="thread-artifact-outputs-toggle"
        expanded={isSectionOpen('outputs')}
        onToggle={() => handleToggleSection('outputs')}
        statusChrome={pendingDraftsStatus}
      >
        {livingIsLoading && artifacts.length === 0 ? (
          <Text size="sm" color="muted" className="px-1 py-1">
            {t('threads.detail.rail.loadingOutputs')}
          </Text>
        ) : livingFailed ? (
          <div
            className="space-y-2 px-1 py-1"
            data-testid="thread-artifact-living-error"
          >
            <Text size="sm" color="muted">
              {t('threads.detail.rail.loadOutputsFailed')}
            </Text>
            {onRetryLiving ? (
              <button
                type="button"
                className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
                data-testid="thread-artifact-living-retry"
                onClick={() => onRetryLiving()}
              >
                {tCommon('retry')}
              </button>
            ) : null}
          </div>
        ) : artifacts.length === 0 ? (
          <Text size="sm" color="muted" className="px-1 py-1">
            {t('threads.detail.rail.noOutputs')}
          </Text>
        ) : (
          <div className="space-y-2">
            {artifacts.map((item, index) => (
              <ArtifactCard
                key={item.key}
                item={item}
                index={index}
                onOpenFullscreen={onOpenArtifactFullscreen}
                threadAssets={assets}
                docsByToken={docsByToken}
                onOpenCitation={onOpenCitation}
                onOpenStudyReport={onOpenStudyReport}
                plots={plots}
                quotes={quotes}
                populationId={populationId}
                strips={strips}
              />
            ))}
          </div>
        )}
      </RailCollapsible>

      {hasAssets && assets ? (
        <RailCollapsible
          label={assetsLabel}
          toggleTestId="thread-artifact-assets-toggle"
          expanded={isSectionOpen('assets')}
          onToggle={() => handleToggleSection('assets')}
          sectionTestId="thread-artifact-assets-section"
        >
          <ThreadAssetTiles assets={assets} onRemoveAsset={onRemoveAsset} />
        </RailCollapsible>
      ) : null}

      {hasSources ? (
        <RailCollapsible
          label={t('threads.detail.rail.contextCount', {
            count: calculations.length,
          })}
          toggleTestId="thread-artifact-context-toggle"
          expanded={isSectionOpen('context')}
          onToggle={() => handleToggleSection('context')}
        >
          <ThreadCalculationSources
            calculations={calculations}
            highlightedId={focusedCalculation?.evidenceId}
            focusNonce={focusedCalculation?.nonce}
          />
        </RailCollapsible>
      ) : null}

      {showProgress ? (
        <RailCollapsible
          label={t('threads.detail.rail.progress')}
          toggleTestId="thread-artifact-progress-toggle"
          expanded={isSectionOpen('progress')}
          onToggle={() => handleToggleSection('progress')}
        >
          <ThreadRunPane
            run={run}
            template={template}
            isLoading={isLoading}
            error={error}
            includeGateCards={false}
            onRetryStep={onRetryStep}
            onSubmitGate={onSubmitGate}
            onCancelRun={onCancelRun}
            language={language}
          />
        </RailCollapsible>
      ) : null}
    </div>
  )
}
